import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Paper
} from '@mui/material';
import { apiService } from '../services/api';
import { Profile } from '../types';

const About: React.FC = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profiles = await apiService.getProfile();
        if (profiles.length > 0) {
          setProfile(profiles[0]);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">Information not available yet.</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3,
            }}
          >
            SHORT PROFILE
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
            {profile.short_profile}
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.2)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    color: 'white',
                    '& *': {
                      color: 'white !important'
                    },
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      my: 2,
                    },
                    '& figure': {
                      margin: '24px 0',
                      width: '100% !important'
                    },
                    '& p': {
                      mb: 2
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: profile.mission }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #3bf372ff 0%, #b5f3cbff 100%)',
                color: '#166534',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.1)',
                border: '1px solid #bbf7d0',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Our Vision
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  {profile.vision}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ 
          p: 6, 
          borderRadius: 2, 
          boxShadow: '0 4px 24px rgba(18, 204, 12, 0.99)',
          // background: 'linear-gradient(135deg, #065f46 0%, #059669 100%)',
          color: 'white'
        }}>
          <Typography variant="h4" gutterBottom sx={{ 
            fontWeight: 700, 
            mb: 4,
            pb: 2,
            borderBottom: '2px solid #34d399',
            display: 'inline-block'
          }}>
            Our Story
          </Typography>
          <Typography
            variant="body1"
            component="div"
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'white',
              '& *': {
                color: 'white !important'
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                my: 2,
              },
              '& figure': {
                margin: '24px 0',
                width: '100% !important'
              },
              '& p': {
                mb: 2
              }
            }}
            dangerouslySetInnerHTML={{ __html: profile.story }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default About;
